import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listB2bProducts } from "../../../graphql/queries/listB2bProducts";
import "./b2bdtc-specifications.scss";

const ProductSelect = ({
  mode = "single",
  selectedProducts = [],
  onSelect,
  onRemove,
  disabled = false,
  error: externalError,
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const containerRef = useRef(null);

  const { data, loading } = useQuery(listB2bProducts, {
    fetchPolicy: "cache-first",
  });

  const products = useMemo(
    () =>
      (data?.listKeywordGroups || []).map((p) => ({
        id: String(p.metadata?.product_id ?? p.id),
        name: p.name,
        code: String(p.metadata?.product_code ?? p.slug),
        category: p.category || "",
      })),
    [data]
  );

  const filteredProducts = useMemo(() => {
    if (!searchText.trim()) return products;
    const q = searchText.toLowerCase();
    return products.filter(
      (p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q)
    );
  }, [products, searchText]);

  const selectedIds = useMemo(
    () => new Set(selectedProducts.map((p) => p.id)),
    [selectedProducts]
  );

  // fetched product catalog so the selection isn't shown as empty.
  const resolvedSelectedProducts = useMemo(
    () =>
      selectedProducts.map((p) => {
        if (p.name) return p;
        const match = products.find(
          (candidate) => candidate.id === p.id || candidate.code === p.code
        );
        return match
          ? { ...p, name: match.name, category: p.category || match.category }
          : p;
      }),
    [selectedProducts, products]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
    setSearchText("");
  }, [disabled]);

  const handleSelect = useCallback(
    (product) => {
      if (mode === "single") {
        onSelect(product);
        setOpen(false);
        setSearchText("");
      } else {
        if (selectedIds.has(product.id)) {
          onRemove?.(product.id);
        } else {
          onSelect(product);
        }
      }
    },
    [mode, onSelect, onRemove, selectedIds]
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      if (mode === "single" && selectedProducts.length > 0) {
        onRemove?.(selectedProducts[0].id);
      }
    },
    [mode, selectedProducts, onRemove]
  );

  const displayValue = useMemo(() => {
    if (resolvedSelectedProducts.length === 0) return null;
    if (mode === "single") return resolvedSelectedProducts[0]?.name;
    return `${resolvedSelectedProducts.length} product${resolvedSelectedProducts.length !== 1 ? "s" : ""} selected`;
  }, [resolvedSelectedProducts, mode]);

  const selectedSingle = mode === "single" ? resolvedSelectedProducts[0] : null;

  return (
    <div
      className={`b2bdtc-product-select ${disabled ? "disabled" : ""}`}
      ref={containerRef}
    >
      <div className="b2bdtc-field-header">
        <span className="b2bdtc-field-label">Product</span>
        <span className="b2bdtc-field-required">*Required</span>
      </div>
      <div
        className={`b2bdtc-control ${open ? "open" : ""} ${disabled ? "disabled" : ""} ${externalError ? "error" : ""}`}
        onClick={handleToggle}
        data-testid="product-select-control"
      >
        <span
          className={`b2bdtc-control-value ${!displayValue ? "placeholder" : ""}`}
        >
          {displayValue || "Select Product"}
        </span>
        {mode === "single" && selectedProducts.length > 0 && !disabled && (
          <span
            className="b2bdtc-control-clear"
            onClick={handleClear}
            data-testid="product-select-clear"
          >
            &times;
          </span>
        )}
        <span className="b2bdtc-control-chevron">&#9662;</span>
      </div>
      {mode === "single" && selectedSingle && (
        <div className="b2bdtc-product-hint" data-testid="product-hint">
          {selectedSingle.code}
          {selectedSingle.category ? ` \u00B7 ${selectedSingle.category}` : ""}
        </div>
      )}
      {open && (
        <div className="b2bdtc-menu" data-testid="product-select-menu">
          <div className="b2bdtc-menu-search">
            <input
              type="text"
              placeholder="Search by product name or code"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              data-testid="product-search-input"
            />
          </div>
          <div className="b2bdtc-menu-list">
            {loading ? (
              <div className="b2bdtc-menu-empty">Loading products...</div>
            ) : filteredProducts.length === 0 ? (
              <div className="b2bdtc-menu-empty">No product found</div>
            ) : (
              filteredProducts.map((product) => {
                const isSelected = selectedIds.has(product.id);
                return (
                  <div
                    key={product.id}
                    className={`b2bdtc-menu-option ${isSelected ? "selected" : ""}`}
                    onClick={() => handleSelect(product)}
                    data-testid={`product-option-${product.id}`}
                  >
                    {mode === "multi" && (
                      <span
                        className={`b2bdtc-checkbox ${isSelected ? "checked" : ""}`}
                      >
                        {isSelected && "\u2713"}
                      </span>
                    )}
                    <div className="b2bdtc-option-text">
                      <span className="b2bdtc-option-title">{product.name}</span>
                      <span className="b2bdtc-option-subtitle">
                        {product.code}
                        {product.category ? ` \u2013 ${product.category}` : ""}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="b2bdtc-menu-footer">
            <span>Products retrieved from PepSell</span>
            <span>{mode === "single" ? "single select" : "multi select"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelect;
